import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import type { DeadlineFormInterface } from '@zaoblako/shared/common/dtos/deadline/deadline-form.dto';
import type { PlanningUnitFormInterface } from '@zaoblako/shared/common/dtos/planning-unit/planning-unit-form.dto';
import { DeadlineTypeEnum } from '@zaoblako/shared/common/enums/deadline.type.enum';
import { TemplateEntityPeriodicityEnum } from '@zaoblako/shared/common/enums/template.entity.periodicity.enum';
import type { ApprovalSchemeFindInterface } from '@zaoblako/shared/modules/catalog/dtos/approval-scheme/approval-scheme-find.dto';
import type { BankFindInterface } from '@zaoblako/shared/modules/catalog/dtos/bank/bank-find.dto';
import type { CurrencyFindInterface } from '@zaoblako/shared/modules/catalog/dtos/currency/currency-find.dto';
import type { DirectionFindInterface } from '@zaoblako/shared/modules/catalog/dtos/direction/direction-find.dto';
import type { FilialFindInterface } from '@zaoblako/shared/modules/catalog/dtos/filial/filial-find.dto';
import { DirectionKindEnum } from '@zaoblako/shared/modules/catalog/enums/direction.kind.enum';
import type { BankDetailsFindInterface } from '@zaoblako/shared/modules/client/dtos/bank.details/bank-details-find.dto';
import type { CompanyFindInterface } from '@zaoblako/shared/modules/client/dtos/company/find/company-find.dto';
import type { NdsFindInterface } from '@zaoblako/shared/modules/finance/dtos/nds-find.dto';
import type { UserFindInterface } from '@zaoblako/shared/modules/security/dtos/user/user-find.dto';
import { UserGroupMultiSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/user-group-multi-select/user-group-multi-select.component';
import type { TaskStatusFindInterface } from '@zaoblako/shared/modules/task/dtos/status/task-status-find.dto';
import type { TaskFindManyQueryInterface } from '@zaoblako/shared/modules/task/dtos/task/task-find-many-query.dto';
import type { TaskFindInterface } from '@zaoblako/shared/modules/task/dtos/task/task-find.dto';
import type { TaskPriorityFindInterface } from '@zaoblako/shared/modules/task/dtos/task-priority-find.dto';
import type { TaskTemplateFindInterface } from '@zaoblako/shared/modules/task/dtos/template/task-template-find.dto';
import {
  TaskTemplateFormDto,
  type TaskTemplateFormInterface
} from '@zaoblako/shared/modules/task/dtos/template/task-template-form.dto';
import { TaskKindEnum } from '@zaoblako/shared/modules/task/enum/task-kind.enum';
import { TaskPaymentTypeEnum } from '@zaoblako/shared/modules/task/enum/task.payment.type.enum';
import { TaskStatusTypeEnum } from '@zaoblako/shared/modules/task/enum/task.status.type.enum';
import { CrmTableColumn } from '@zaoblako/web/core/models/crm-table-column.interface';
import { AccessService } from '@zaoblako/web/core/models/enums/access-rules/access.service';
import { BasisClientService } from '@zaoblako/web/core/services/basis/basis-client.service';
import { ApprovalSchemeService } from '@zaoblako/web/core/services/budget/approval-scheme.service';
import { BankService } from '@zaoblako/web/core/services/catalog/bank.service';
import { ConfirmDialogService } from '@zaoblako/web/core/services/confirm-dialog.service';
import { ErrorService } from '@zaoblako/web/core/services/error.service';
import { DirectionService } from '@zaoblako/web/core/services/finance/direction.service';
import { FilialService } from '@zaoblako/web/core/services/finance/filial.service';
import { NdsService } from '@zaoblako/web/core/services/finance/nds.service';
import { GlobalService } from '@zaoblako/web/core/services/global.service';
import { BrsPrefixService } from '@zaoblako/web/core/services/prefix/brs-prefix.service';
import { QueryParamsService } from '@zaoblako/web/core/services/query-params.service';
import { TaskPriorityService } from '@zaoblako/web/core/services/task/task-priority.service';
import { TaskStatusService } from '@zaoblako/web/core/services/task/task-status.service';
import { TaskTemplateService } from '@zaoblako/web/core/services/task/task-template.service';
import { TaskService } from '@zaoblako/web/core/services/task/task.service';
import { ContragentAutoCompleteComponent } from '@zaoblako/web/shared/dumbs/autoComplete/contragent-auto-complete/contragent-auto-complete.component';
import { TaskTemplateHistoryWidgetComponent } from '@zaoblako/web/modules/payments/dumbs/task-template-history-widget/task-template-history-widget.component';
import { FormType } from '@zaoblako/web/shared/components/form-sidebar/models/form-state';
import { FormStoreService } from '@zaoblako/web/shared/components/form-sidebar/services/form-store.service';
import { DealAutoCompleteComponent } from '@zaoblako/web/shared/dumbs/autoComplete/deal-auto-complete/deal-auto-complete.component';
import { BaseFormComponent } from '@zaoblako/web/shared/dumbs/base-form/base-form.component';
import { ButtonComponent } from '@zaoblako/web/shared/dumbs/button/button.component';
import { DatepickerComponent } from '@zaoblako/web/shared/dumbs/datepicker/datepicker.component';
import { ApprovalSchemeSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/approval-scheme-single-select/approval-scheme-single-select.component';
import { BudgetClassificationCodeSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/budget-classification-code-single-select/budget-classification-code-single-select.component';
import { BankDetailsSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/client/bank-details-single-select/bank-details-single-select.component';
import { CompanySingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/company-single-select/company-single-select.component';
import { CostCenterSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/cost-center-single-select/cost-center-single-select.component';
import { CurrencySingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/currency-single-select/currency-single-select.component';
import { DirectionSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/direction-single-select/direction-single-select.component';
import { FilialSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/filial-single-select/filial-single-select.component';
import { PayerStatusSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/payer-status-single-select/payer-status-single-select.component';
import { SingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/single-select/single-select.component';
import { UnitSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/unit-single-select/unit-single-select.component';
import { UserMultiSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/user-multi-select/user-multi-select.component';
import { UserSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/user-single-select/user-single-select.component';
import { InputTextComponent } from '@zaoblako/web/shared/dumbs/input-text/input-text.component';
import { PlanningDeadlineFormComponent } from '@zaoblako/web/shared/dumbs/planning-deadline-form/planning-deadline-form.component';
import { PlanningPeriodicityCardComponent } from '@zaoblako/web/shared/dumbs/planning-periodicity-card/planning-periodicity-card.component';
import { SpinnerComponent } from '@zaoblako/web/shared/dumbs/spinner/spinner.component';
import { TableComponent } from '@zaoblako/web/shared/dumbs/table/table.component';
import { TemplateSubjectInputComponent } from '@zaoblako/web/shared/dumbs/template-subject-input/template-subject-input.component';
import { UserCardPreviewComponent } from '@zaoblako/web/shared/dumbs/user-card-preview/user-card-preview.component';
import { JoinPipe } from '@zaoblako/web/shared/pipes/join.pipe';
import { PaymentTypePipe } from '@zaoblako/web/shared/pipes/payment-type.pipe';
import { SortTaskStatusByOrderPipe } from '@zaoblako/web/shared/pipes/statuses/sort-task-status-by-order.pipe';
import { TaskKindNamePipe } from '@zaoblako/web/shared/pipes/task-kind-name.pipe';
import type { ContragentFindInterface } from '@zaoblako/shared/modules/client/dtos/contragent/contragent-find.dto';

import moment from 'moment-timezone';
import { CardModule } from 'primeng/card';
import { CheckboxModule } from 'primeng/checkbox';
import { DividerModule } from 'primeng/divider';
import { InputNumberModule } from 'primeng/inputnumber';
import { forkJoin, Observable, Subscription } from 'rxjs';

@Component({
  standalone: true,
  selector: 'crm-task-template-form',
  templateUrl: 'task-template-form.component.html',
  imports: [
    CommonModule,
    CardModule,
    InputTextComponent,
    JoinPipe,
    CheckboxModule,
    FormsModule,
    ContragentAutoCompleteComponent,
    SingleSelectComponent,
    DirectionSingleSelectComponent,
    PaymentTypePipe,
    TemplateSubjectInputComponent,
    InputNumberModule,
    PlanningDeadlineFormComponent,
    PlanningPeriodicityCardComponent,
    DividerModule,
    UserGroupMultiSelectComponent,
    TaskTemplateHistoryWidgetComponent,
    ButtonComponent,
    SpinnerComponent,
    DealAutoCompleteComponent,
    UserCardPreviewComponent,
    UnitSingleSelectComponent,
    FilialSingleSelectComponent,
    ApprovalSchemeSingleSelectComponent,
    UserSingleSelectComponent,
    UserMultiSelectComponent,
    CostCenterSingleSelectComponent,
    TableComponent,
    RouterLink,
    DatepickerComponent,
    CurrencySingleSelectComponent,
    BankDetailsSingleSelectComponent,
    TaskKindNamePipe,
    BudgetClassificationCodeSingleSelectComponent,
    PayerStatusSingleSelectComponent,
    SortTaskStatusByOrderPipe,
    CompanySingleSelectComponent
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TaskTemplateFormComponent
  extends BaseFormComponent<TaskTemplateFormInterface>
  implements OnInit, OnDestroy {
  @Input() public readonly index: number;
  public taskTemplate = <TaskTemplateFormInterface>{};
  public dto = TaskTemplateFormDto;
  public form$: Observable<FormType>;
  public loading$: Observable<boolean>;

  public directions: DirectionFindInterface[] = [];
  public priorities: TaskPriorityFindInterface[] = [];
  public nds: NdsFindInterface[] = [];
  public banks: BankFindInterface[] = [];
  public schemes: ApprovalSchemeFindInterface[] = [];
  public currencies: CurrencyFindInterface[] = [];
  public statuses: TaskStatusFindInterface[] = [];
  public filials: FilialFindInterface[] = [];
  public bankDetails: BankDetailsFindInterface[] = [];
  public contragentBankDetails: BankDetailsFindInterface[] = [];

  public kinds: TaskKindEnum[] = Object.values(TaskKindEnum);

  public loadingTasks = true;
  public tasks: TaskFindInterface[] = [];
  public readonly taskColumns: CrmTableColumn[] = [{ label: 'Номер' }, { label: 'Дата' }];

  public readonly types = Object.values(TaskPaymentTypeEnum);
  public today = moment().format('YYYY-MM-DD');

  private querySubscription$: Subscription;

  public get TaskKindEnum() {
    return TaskKindEnum;
  }

  public get accessRights() {
    return {
      canReadBrsTask: this.accessService.brs.brsTaskRights.canReadBrsTask,
      canReadDeal: this.accessService.finance.dealRights.canReadDeal
    };
  }

  public constructor(
    private readonly formStoreService: FormStoreService,
    private readonly taskService: TaskService,
    private readonly taskTemplateService: TaskTemplateService,
    private readonly confirmService: ConfirmDialogService,
    private readonly filialService: FilialService,
    private readonly directionService: DirectionService,
    private readonly taskPriorityService: TaskPriorityService,
    private readonly ndsService: NdsService,
    private readonly bankService: BankService,
    private readonly approvalSchemeService: ApprovalSchemeService,
    private readonly taskStatusService: TaskStatusService,
    private readonly errorService: ErrorService,
    public readonly globalService: GlobalService,
    private readonly cdRef: ChangeDetectorRef,
    private readonly queryParamsService: QueryParamsService,
    private readonly basisClientService: BasisClientService,
    public readonly brsPrefixService: BrsPrefixService,
    public readonly accessService: AccessService
  ) {
    super();
  }

  public ngOnInit() {
    const taskId = this.formStoreService.getState.forms[this.index]?.data?.taskId;

    this.form$ = this.formStoreService.select((state) => state.forms[this.index]);
    this.loading$ = this.formStoreService.select((state) => state.forms[this.index]?.loading);

    if (!isNaN(taskId)) {
      this.taskService.getTemplateFromTask(taskId).subscribe({
        next: (template) => {
          this.taskTemplate = template;
          this.cdRef.markForCheck();
        }
      });
    }

    forkJoin([
      this.taskStatusService.find<TaskStatusFindInterface>(),
      this.filialService.findPage(),
      this.directionService.find<DirectionFindInterface>({
        kinds: [DirectionKindEnum.EXPENSE, DirectionKindEnum.DIVIDENTS, DirectionKindEnum.INCOME_TAX]
      }),
      this.taskPriorityService.find<TaskPriorityFindInterface>(),
      this.approvalSchemeService.findPage({ onlyWithExplicitAccess: true })
    ]).subscribe(([statuses, { items: filials }, directions, priorities, { items: schemes }]) => {
      this.statuses = statuses.filter((status) =>
        [TaskStatusTypeEnum.DRAFT, TaskStatusTypeEnum.SCHEDULED, TaskStatusTypeEnum.ON_APPROVAL].includes(status.type)
      );
      this.filials = filials;
      this.directions = directions;
      this.priorities = priorities;
      this.schemes = schemes;

      if (this.taskTemplate.filial) {
        this.taskTemplate.filial = this.filials?.find((filial) => filial?.id === this.taskTemplate.filial?.id);
      }

      if (!this.formStoreService.getState.forms[this.index]?.isEdit) {
        if (this.taskTemplate.organization) {
          this.basisClientService
            .bankDetailsSearch({
              companyIds: [this.taskTemplate.organization?.id],
              onlyForCompanies: true
            })
            .subscribe((bankDetails) => {
              this.bankDetails = bankDetails;
              this.cdRef.markForCheck();
            });
        }
        if (!this.taskTemplate.scheme) {
          this.taskTemplate.scheme = this.schemes?.find((scheme) => scheme.isDefault);
          this.changeUsersAndGroups(this.taskTemplate.scheme);
        }
        if (!this.taskTemplate.status) {
          this.taskTemplate.status = this.statuses?.find((status) => status.type === TaskStatusTypeEnum.SCHEDULED);
        }
        this.taskTemplate = { ...this.taskTemplate };
      }
      this.cdRef.markForCheck();
    });

    this.ndsService.find<NdsFindInterface>().subscribe((nds) => {
      this.nds = nds;
      this.cdRef.markForCheck();
    });

    if (this.formStoreService.getState.forms[this.index]?.isEdit) {
      this.getById();
    } else {
      this.taskTemplate.planning = <PlanningUnitFormInterface>{};
      this.taskTemplate.planning.periodicity = TemplateEntityPeriodicityEnum.NONE;

      this.taskTemplate.deadline = <DeadlineFormInterface>{};
      this.taskTemplate.deadline.daysAfter = 0;
      this.taskTemplate.deadline.type = DeadlineTypeEnum.RELATIVE;

      this.taskTemplate.documentDate = <DeadlineFormInterface>{};
      this.taskTemplate.documentDate.daysAfter = 0;
      this.taskTemplate.documentDate.type = DeadlineTypeEnum.RELATIVE;

      this.taskTemplate.kind = TaskKindEnum.PAYMENT_TO_SUPPLIER;
    }
  }

  public ngOnDestroy(): void {
    this.querySubscription$?.unsubscribe();
  }

  public getById() {
    this.formStoreService.setLoading(true, this.index);

    forkJoin([
      this.taskTemplateService.findById<TaskTemplateFindInterface>(
        this.formStoreService.getState.forms[this.index]?.entityId
      ),
      this.approvalSchemeService.findPage()
    ]).subscribe(([taskTemplate, { items: schemes }]) => {
      this.taskTemplate = taskTemplate;
      this.taskTemplate.userAuditors = (taskTemplate.userAuditors || []).map((user) =>
        this.withAuditorDisplayProfile(user)
      );
      this.taskTemplate.scheme = schemes?.find((scheme) => this.taskTemplate.scheme?.id === scheme?.id);
      this.formStoreService.setLoading(false, this.index);
      this.cdRef.markForCheck();

      if (this.taskTemplate.contragent) {
        this.basisClientService
          .bankDetailsSearch({
            contragentIds: [this.taskTemplate.contragent?.id]
          })
          .subscribe((bankDetails) => {
            this.contragentBankDetails = bankDetails;
            this.cdRef.markForCheck();
          });
      }

      if (this.taskTemplate.organization) {
        this.basisClientService
          .bankDetailsSearch({
            companyIds: [this.taskTemplate.organization?.id],
            onlyForCompanies: true
          })
          .subscribe((bankDetails) => {
            this.bankDetails = bankDetails;
            this.cdRef.markForCheck();
          });
      }

      if (this.accessRights.canReadBrsTask) {
        const templateId = this.formStoreService.getState.forms[this.index]?.entityId;

        this.taskService.findMany({ templateIds: [templateId], ignorePeriod: true }).subscribe({
          next: (tasks) => {
            this.tasks = tasks.items;
            this.loadingTasks = false;
            this.cdRef.markForCheck();
          },
          error: () => {
            this.loadingTasks = false;
            this.cdRef.markForCheck();
          }
        });
      } else {
        this.tasks = [];
        this.loadingTasks = false;
      }
    });
  }

  public saveTemplate() {
    const requiredAnalyticalFeatures = this.globalService.globalInfo?.requiredAnalyticalFeatures;
    if (
      (requiredAnalyticalFeatures?.directionsRequired && !this.taskTemplate.direction) ||
      (requiredAnalyticalFeatures?.unitsRequired && !this.taskTemplate.unit) ||
      (requiredAnalyticalFeatures?.filialsRequired && !this.taskTemplate.filial)
    ) {
      this.errors = Object.create({});

      if (requiredAnalyticalFeatures?.directionsRequired && !this.taskTemplate.direction) {
        this.errors.direction = 'Бюджетная статья не выбрана' as any;
      }
      if (requiredAnalyticalFeatures?.unitsRequired && !this.taskTemplate.unit) {
        this.errors.unit = 'Бизнес единица не выбрана' as any;
      }
      if (requiredAnalyticalFeatures?.filialsRequired && !this.taskTemplate.filial) {
        this.errors.filial = 'Филиал не выбран' as any;
      }
      return;
    }

    if (this.checkValidate(this.taskTemplate).valid) {
      if (this.formStoreService.getState.forms[this.index]?.entityId) {
        this.formStoreService.setLoading(true, this.index);

        this.taskTemplateService
          .update<TaskTemplateFormInterface, TaskTemplateFindInterface>(this.taskTemplate)
          .subscribe({
            next: (template) => {
              this.formStoreService.setLoading(false, this.index);
              this.errorService.addSuccessMessage(`Шаблон "${template.name}" обновлен`);
              this.formStoreService.saveNext(template, this.index);
            },
            error: () => {
              this.formStoreService.setLoading(false, this.index);
            }
          });
      } else {
        this.formStoreService.setLoading(true, this.index);

        this.taskTemplateService.create<TaskTemplateFindInterface>(this.taskTemplate).subscribe({
          next: (template) => {
            this.formStoreService.setLoading(false, this.index);
            this.errorService.addSuccessMessage('Шаблон создан');
            this.formStoreService.saveNext(template, this.index);
          },
          error: () => {
            this.formStoreService.setLoading(false, this.index);
          }
        });
      }
    }
  }

  public deleteTemplate() {
    this.confirmService.confirm({
      message: `Вы действительно хотите удалить шаблон "${this.taskTemplate.name}"?`,
      accept: () => {
        this.formStoreService.setLoading(true, this.index);
        this.cdRef.markForCheck();

        this.taskTemplateService
          .deleteById(this.formStoreService.getState.forms[this.index]?.entityId)
          .subscribe(() => {
            this.errorService.addSuccessMessage(`Шаблон "${this.taskTemplate.name}" удален`);
            this.formStoreService.deleteNext(this.index);
          });
      }
    });
  }

  public searchBank(name: string) {
    this.bankService.search({ search: name }).subscribe((banks) => {
      this.banks = banks;
      this.cdRef.markForCheck();
    });
  }

  public changeUsersAndGroups(scheme: ApprovalSchemeFindInterface) {
    this.taskTemplate.userAuditors = [];
    this.taskTemplate.userGroupAuditors = [];
    if (scheme?.userAuditors?.length) {
      this.taskTemplate.userAuditors = scheme.userAuditors.map((user) => this.withAuditorDisplayProfile(user));
    }
    if (scheme?.userGroupAuditors?.length) {
      this.taskTemplate.userGroupAuditors = scheme.userGroupAuditors;
    }
    this.cdRef.markForCheck();
  }

  /** Ensure multi-select chips have `profile.name` when API only returns top-level `name`. */
  private withAuditorDisplayProfile(user: UserFindInterface): UserFindInterface {
    if (user.profile?.name) {
      return user;
    }

    return {
      ...user,
      profile: { ...user.profile, name: user.name || user.profile?.name || '' } as UserFindInterface['profile']
    };
  }

  public toTask(task: any): TaskFindInterface {
    return task as TaskFindInterface;
  }

  public directToTaskList() {
    const queryParams: TaskFindManyQueryInterface = {
      templateIds: [this.formStoreService.getState.forms[this.index]?.entityId],
      ignorePeriod: true
    };

    this.querySubscription$ = this.queryParamsService
      .getUrlTreeWithDigest(['/payments/task'], queryParams, true)
      .subscribe({
        next: (urlTree) => {
          const url = String(urlTree);
          window.open(url, '_blank');
        },
        error: (err) => {
          console.error('Failed to generate URL with digest:', err);
          this.errorService.addCustomError('Ошибка', 'Произошла ошибка, вернитесь на главную и попробуйте снова.');
        }
      });
  }

  public changeOrganization(organization?: CompanyFindInterface) {
    const org = organization ?? this.taskTemplate.organization;

    if (!org?.id) {
      this.currencies = [];
      this.bankDetails = [];
      this.taskTemplate.organization = null;
      this.taskTemplate.currency = null;
      this.taskTemplate.organizationBankDetails = null;
      this.cdRef.markForCheck();
      return;
    }

    this.taskTemplate.organization = org;
    this.currencies = [];
    this.bankDetails = [];
    this.taskTemplate.currency = null;
    this.taskTemplate.organizationBankDetails = null;

    if (org.currencies?.length) {
      this.currencies = org.currencies;
      setTimeout(() => {
        this.taskTemplate.currency = this.currencies.find((currency) => currency.isDefault);
        this.cdRef.markForCheck();
      });
    }

    this.loadOrganizationBankDetails(org.id, org);
  }

  private loadOrganizationBankDetails(companyId: number, organization?: CompanyFindInterface) {
    this.basisClientService
      .bankDetailsSearch({
        companyIds: [companyId],
        onlyForCompanies: true
      })
      .subscribe((bankDetails) => {
        this.bankDetails = bankDetails;
        const picked = this.pickOrganizationBankDetails(this.bankDetails, organization);
        this.taskTemplate.organizationBankDetails = picked
          ? this.bankDetails.find((bankDetail) => bankDetail.id === picked.id) ?? picked
          : null;
        this.taskTemplate = { ...this.taskTemplate };
        this.cdRef.markForCheck();
      });
  }

  private pickOrganizationBankDetails(
    bankDetails: BankDetailsFindInterface[],
    organization?: CompanyFindInterface
  ): BankDetailsFindInterface | null {
    if (!bankDetails?.length) {
      return null;
    }

    if (bankDetails.length === 1) {
      return bankDetails[0];
    }

    const defaultFromList = bankDetails.find((bankDetail) => !!bankDetail.isDefault);
    if (defaultFromList) {
      return defaultFromList;
    }

    const defaultId = organization?.bankDetails?.find((bankDetail) => bankDetail.isDefault)?.id;
    if (defaultId) {
      return bankDetails.find((bankDetail) => bankDetail.id === defaultId) ?? null;
    }

    return null;
  }

  public changeKind() {
    if (!this.taskTemplate.kind) {
      this.taskTemplate.kind = null;
    }
  }

  public changeAmount(amount: string | number) {
    this.taskTemplate.amount = Number(amount);
  }

  public onContragentChange(contragent: ContragentFindInterface) {
    if (!contragent?.id) {
      this.taskTemplate.contragentBankDetails = null;
      this.contragentBankDetails = [];
      this.cdRef.markForCheck();
      return;
    }

    this.basisClientService
      .bankDetailsSearch({
        contragentIds: [contragent.id]
      })
      .subscribe((bankDetails) => {
        const defaultBankDetails = bankDetails.length > 1
          ? bankDetails.find((item) => item.isDefault)
          : bankDetails[0];
        this.contragentBankDetails = bankDetails;

        let contragentBankDetails: BankDetailsFindInterface = null;
        if (this.taskTemplate.contragentBankDetails?.id) {
          contragentBankDetails = bankDetails.find((item) => item?.id === this.taskTemplate.contragentBankDetails.id) || defaultBankDetails;
        } else {
          contragentBankDetails = defaultBankDetails;
        }
        this.taskTemplate.contragentBankDetails = contragentBankDetails;

        this.cdRef.markForCheck();
      });
  }
}
